import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { formatMoney, minorFromWire } from '@/lib/money/index.js';
import { moneyView, type MoneyView } from '@/lib/money/display-rate.js';

interface MoneyContextValue {
  currency: string;
  displayCurrency: string;
  rate: string | null;
}

const MoneyContext = createContext<MoneyContextValue>({
  currency: 'THB',
  displayCurrency: 'THB',
  rate: null,
});

/**
 * `currency` is the LEDGER currency every API money figure is in.
 * `displayCurrency` + `rate` (display units per one ledger unit) are what the
 * site shows it in; omitted, money shows as-is.
 */
export function MoneyProvider({
  currency,
  displayCurrency,
  rate = null,
  children,
}: {
  currency: string;
  displayCurrency?: string;
  rate?: string | null;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ currency, displayCurrency: displayCurrency ?? currency, rate }),
    [currency, displayCurrency, rate],
  );
  return <MoneyContext.Provider value={value}>{children}</MoneyContext.Provider>;
}

/** The LEDGER currency — what API amounts are denominated in, never what the site shows. */
export function useMoneyCurrency(): string {
  return useContext(MoneyContext).currency;
}

/**
 * Ledger money as the site shows it. Every website screen formats a ledger
 * amount through `format` (or `<Money>`); `currency` is what that output is in
 * and what a player types an amount in, `toLedger` turns such a typed amount
 * back into ledger minor units (truncated).
 */
export function useDisplayMoney(): MoneyView {
  const { currency, displayCurrency, rate } = useContext(MoneyContext);
  return useMemo(() => moneyView(currency, displayCurrency, rate), [currency, displayCurrency, rate]);
}

/**
 * `formatMoney` for website screens: an amount whose `currency` is omitted or
 * is the LEDGER currency is shown in the display currency; any other currency
 * (an amount that genuinely arrived in kip, say) is formatted as-is.
 */
export function useFormatMoney(): (minor: bigint | number, currency?: string | null) => string {
  const view = useDisplayMoney();
  return useCallback(
    (minor, currency) => {
      if (currency != null && currency.toUpperCase() !== view.ledgerCurrency) return formatMoney(minor, currency);
      const ledgerMinor = minorFromWire(minor);
      // A figure that is not whole minor units is shown as sent, in the currency
      // it was sent in: converting it would first have to round it.
      return ledgerMinor === null ? formatMoney(minor, view.ledgerCurrency) : view.format(ledgerMinor);
    },
    [view],
  );
}
